import Modal from "react-modal";
import api from "@/components/customAxios/Axios";
import { useEffect, useState } from "react";
import { useLoader } from "@/context/loaderContext";
import { useParams } from "react-router-dom";
import RegenerateQuestionModal from "../components/RespJdDash/RegenerateQuestionModal";
import DeleteQuestionModal from "../components/RespJdDash/DeleteQuestionModal";
import UpdateBehaviouralQuestionModal from "../components/RespJdDash/UpdateBehaviouralQuestionModal";
import UpdateTechnicalQuestionModal from "../components/RespJdDash/UpdateTechnicalQuestionModal";
import UpdateCodingQuestionModal from "../components/RespJdDash/UpdateCodingQuestionModal";
import AddNewBehaviouralQuestion from "../components/RespJdDash/AddNewBehaviouralQuestion";
import AddNewTechnicalQuestion from "../components/RespJdDash/AddNewTechnicalQuestion";
import AddNewCodingQuestion from "../components/RespJdDash/AddNewCodingQuestion";
import useBreadCrumb from "@/context/BreadCrumbContext";
import { useToast } from "@/components/toast";
Modal.setAppElement("#root");
interface BehaviourQuestion {
	b_question_id: string | number;
	b_question_text: string;
}

interface TechQuestion {
	question: string;
	options: {
		A: string;
		B: string;
		C: string;
		D: string;
	};
	answer: string;
}

interface CodingQuestion {
	question: string;
	sample_input: string;
	sample_output: string;
}

interface DataType {
	Behaviour_q: BehaviourQuestion[];
	tech_questions: TechQuestion[];
	coding_question: CodingQuestion[];
}

const ViewGeneratedAssessmentPage = () => {
	const { setBreadCrumb } = useBreadCrumb();
	const { setLoading } = useLoader();
	const { id } = useParams();
	const [data, setData] = useState<DataType | undefined>(undefined);
	const toast = useToast();
	const getQuestions = async () => {
		try {
			setLoading(true);
			const response = await api.get(
				`fetch_job_description_questions?job_id=${id}`
			);
			if (response.status === 200) {
				setData(response.data);
			}
		} catch (error: any) {
			console.log(error.message);
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Failed to Fetch Questions",
				description: error.response?.data?.error || error.response?.data?.message || "Unable to load assessment questions. Please try again later.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		setBreadCrumb({
			mainLabel: {
				label: "Applicants",
				link: `respective-dashboard/${id}`,
			},
			currentLabel: "View Assessment",
		});
	}, []);
	useEffect(() => {
		getQuestions();
	}, []);
	return (
		<div className="bg-white p-4 rounded-xl w-full min-h-[85vh] ">
			{data ? (
				<>
					<div className="flex items-center mb-3">
						<button onClick={() => window.history.back()} className="mr-4">
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M10.0156 0.733192L4.23077 6.51522C3.9374 6.7947 3.9374 7.23141 4.23077 7.51089L10.0156 13.2929"
									stroke="#7F7F7F"
									strokeMiterlimit="2.613"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<h2 className="md:text-xl text-left text-lg font-bold text-gray-800 my-4 font-heading">
							Generated Assessment
						</h2>
					</div>
					<div className="space-y-12">
						{/* Behavioral Questions */}
						<section>
							<div className="flex justify-between items-center mb-3">
								<h2 className="text-lg text-gray-500 font-semibold font-subheading">
									Behavioral Questions
								</h2>
								<AddNewBehaviouralQuestion getQuestions={getQuestions} />
							</div>
							<div className="space-y-4 font-body">
								{data.Behaviour_q.map((elem, index) => (
									<div
										className=" p-2 border-1 rounded-lg grid grid-cols-[90%_10%]"
										key={elem.b_question_id}
									>
										<div className="flex  px-2 py-1">
											<span className="text-lg font-medium">{index + 1}.</span>
											<p className="ml-0 md:ml-2 text-lg">
												{elem.b_question_text}
											</p>
										</div>
										<div className="flex justify-end mt-2 gap-3">
											<RegenerateQuestionModal
												questionId={index}
												questionType="behavioral"
												getQuestions={getQuestions}
											/>

											<UpdateBehaviouralQuestionModal
												curentQuestionIndex={index}
												question={elem.b_question_text}
												behaviouralQuestionSet={data.Behaviour_q}
												getQuestions={getQuestions}
											/>

											<DeleteQuestionModal
												getQuestions={getQuestions}
												currentQuestionIndex={index}
												behaviouralQuestionSet={data.Behaviour_q}
												section="behavioral"
											/>
										</div>
									</div>
								))}
							</div>
						</section>

						{/* Technical Questions */}
						<section>
							<div className="flex justify-between items-center mb-3">
								<h2 className="text-lg text-gray-500 font-semibold font-subheading">
									Technical Questions
								</h2>
								<AddNewTechnicalQuestion getQuestions={getQuestions} />
							</div>
							<div className="space-y-6 font-body">
								{data.tech_questions.map((elem, index) => (
									<div
										className=" p-2 border-1 rounded-lg grid grid-cols-[90%_10%]"
										key={index}
									>
										<div>
											<div className=" flex  px-2 py-1">
												<span className="text-lg font-medium">
													{index + 1}.
												</span>
												<p className="ml-2">{elem.question}</p>
											</div>

											<div className="pl-4 ">
												<p className="font-semibold mt-3 mb-1.5">Options:</p>
												<ul className="list-disc pl-6 space-y-2">
													<li>A. {elem.options.A}</li>
													<li>B. {elem.options.B}</li>
													<li>C. {elem.options.C}</li>
													<li>D. {elem.options.D}</li>
												</ul>
											</div>
											<div className="pl-4">
												<p className="font-semibold my-2">Answer:</p>
												<p className="px-3 rounded mb-2">{elem.answer}</p>
											</div>
										</div>
										<div className=" flex justify-end gap-3 mt-2">
											<RegenerateQuestionModal
												questionId={index}
												questionType="technical"
												getQuestions={getQuestions}
											/>

											<UpdateTechnicalQuestionModal
												curentQuestionIndex={index}
												technicalQuestionSet={data.tech_questions}
												question={elem.question}
												options={elem.options}
												answer={elem.answer}
												getQuestions={getQuestions}
											/>

											<DeleteQuestionModal
												getQuestions={getQuestions}
												currentQuestionIndex={index}
												technicalQuestionSet={data.tech_questions}
												section="technical"
											/>
										</div>
									</div>
								))}
							</div>
						</section>

						{/* Coding Questions */}
						<section>
							<div className="flex justify-between items-center mb-3">
								<h2 className="text-lg text-gray-500 font-semibold font-subheading">
									Coding Questions
								</h2>
								<AddNewCodingQuestion getQuestions={getQuestions} />
							</div>
							<div className="space-y-6 font-body">
								{data.coding_question.map((elem, index) => (
									<div
										className=" p-2 border-1 rounded-lg grid grid-cols-[90%_10%]"
										key={index}
									>
										<div>
											<div className=" flex  px-2 py-1">
												<span className="text-lg font-medium">
													{index + 1}.
												</span>
												<p className="ml-2">{elem.question}</p>
											</div>
											<div className="pl-4 space-y-1 mt-2">
												<p className="font-semibold">Sample Input:</p>
												<p className="p-2">{elem.sample_input}</p>
												<p className="font-semibold mt-2">Sample Output:</p>
												<p className="p-2">{elem.sample_output}</p>
											</div>
										</div>
										<div className=" flex justify-end gap-3 mt-2">
											<RegenerateQuestionModal
												questionId={index}
												getQuestions={getQuestions}
												questionType="coding"
											/>

											<UpdateCodingQuestionModal
												currentQuestionIndex={index}
												question={elem.question}
												sampleInput={elem.sample_input}
												sampleOutput={elem.sample_output}
												codingQuestionSet={data.coding_question}
												getQuestions={getQuestions}
											/>

											<DeleteQuestionModal
												getQuestions={getQuestions}
												currentQuestionIndex={index}
												codingQuestionSet={data.coding_question}
												section="coding"
											/>
										</div>
									</div>
								))}
							</div>
						</section>
					</div>
				</>
			) : (
				<p className="text-3xl text-center">Loading Assessment ...</p>
			)}
		</div>
	);
};

export default ViewGeneratedAssessmentPage;
